import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { logEvent } from '@amplitude/analytics-browser';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '../../../componentsv2/Typography';
import TopBar from '../../../componentsv2/TopBar';
import MarcoLogo from '../../../componentsv2/MarcoLogo';
import { SelectField, TextField } from '../../../componentsv2/Field';
import Button from '../../../componentsv2/Button';
import {
  CheckCircleOutlineOutlined as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import Form from '../../../componentsv2/Form';
import CountryFlag from '../../../componentsv2/CountryFlag';
import ErrorMessage from '../../../components/ErrorMessage';
import { ButtonSizes } from '../../../componentsv2/Button';
import { InvoicesTableIcon } from '../../../components/Icons';
import { useTranslation } from '../../../hooks/i18n';
import { useUserLocation } from '../../../hooks/useUserLocation';
import { useAnonymousUser } from '../../../hooks/anonymous';
import { loadUser as loadUserThunk, logout, useAppDispatch, useAppSelector } from '../../../store';
import { CountryKeys } from '../../../types';
import {
  hasDigit,
  hasLowerCase,
  hasUpperCase,
  OTHER_PHONE_COUNTRY,
  phoneCountryCodes,
  resetStore,
} from '../../../utils/helpers';
import { signupEvents } from '../../../constants/events';
import {
  REFRESH_TOKEN_STORAGE_KEY,
  ACCESS_TOKEN_STORAGE_KEY,
  LOGIN,
  DASHBOARD,
} from '../../../constants/auth';
import { COLORS } from '../../../themev2';
import PhoneNumberField from '../../../componentsv2/Field/PhoneNumberField';
import { acceptInvitation, signupWithInvitation } from '../../../services/auth';
import { currentUserDetail } from '../../../services/companyUsers';
import { identifyUser } from '../../../utils/tracking';
import { useSnackbar } from '../../../context';
import TermsAndConditionsLabel from '../../../componentsv2/TermsAndConditionsLabel';
import SimpleBackdrop from '../../../components/Backdrop';

export type InvitationSignupValues = {
  phoneNumber: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumberCountry: string;
};

const InvitationSignup = () => {
  const { t } = useTranslation(['user', 'common']);
  const { language } = useUserLocation();
  const { location } = useAnonymousUser();
  const { error, status } = useAppSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const token = queryParams.get('token');
  const companyName = queryParams.get('company_name');
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const redirectOnTokenError = () => {
    showSnackbar(t(`common:signup.invitationErrors.object_doesnt_exist`), 'error');
    navigate(LOGIN);
  };

  useEffect(() => {
    logEvent(signupEvents.VIEW_INVITATION_SIGNUP, { Company: companyName });
    // logout
    dispatch(logout())
      .unwrap()
      .then(() => {
        resetStore(dispatch);
      });
    // automatically accept the invitation
    // if it fails, the token is invalid. If result.success is true redirect to home, else do sign up
    setIsPageLoading(true);
    if (!token) {
      redirectOnTokenError();
    } else {
      acceptInvitation(token)
        .then((data) => {
          if (data.success) {
            navigate(`${DASHBOARD}/?companyId=${data.companyId}`);
          }
          setIsPageLoading(false);
        })
        .catch(() => {
          redirectOnTokenError();
        });
    }
  }, []);

  const submit = (values: any) => {
    if (!token) return;
    setIsLoading(true);
    signupWithInvitation({ ...values, token, contactPhone: values.phoneNumber })
      .then(({ refresh, access }) => {
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, access);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh);
        setIsLoading(false);
        dispatch(loadUserThunk())
          .unwrap()
          .then(() => {
            currentUserDetail().then((companyUser) => {
              identifyUser({
                country: companyUser.selectedCompany.country,
                companyName: companyUser.selectedCompany.name,
                name: companyUser.name,
                email: companyUser.user.email,
                language,
              }).promise.then(() => {
                logEvent(signupEvents.SUBMIT_INVITATION_SIGNUP, { Company: companyName });
              });
            });
          });
        navigate(DASHBOARD);
      })
      .catch((error) => {
        showSnackbar(t(`common:signup.invitationErrors.${error.response.data.error_code}`), 'error');
        setIsLoading(false);
      });
  };

  if (isPageLoading) {
    return <SimpleBackdrop hasOpaqueBackground isLoading />;
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <TopBar>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton href="/">
            <MarcoLogo />
          </IconButton>
        </Box>
      </TopBar>
      <Box component="main" sx={{ flexGrow: 1, paddingTop: '1em', display: 'flex' }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1em',
                textAlign: 'center',
              }}
            >
              <InvoicesTableIcon />
              <Box sx={{ position: 'relative', top: '-5em', maxWidth: '60%' }}>
                <Typography sx={{ fontSize: '24px', marginBottom: '0.5em' }}>
                  {t('common:signup.whyMarco')}
                </Typography>
                <Typography sx={{ color: COLORS.gray[1100], fontSize: '18px' }}>
                  {t('common:signup.description')}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            sx={{
              backgroundColor: { xs: 'inherit', md: '#FBFCFE' },
              paddingBottom: '1em',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Grid container rowSpacing={2} justifyContent="center">
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
                  {t('common:signup.title')}
                </Typography>
              </Grid>
              <Grid item xs={10} md={8}>
                <Formik
                  validateOnChange
                  initialValues={{
                    phoneNumber: '',
                    email: queryParams.get('user_email')?.replace(' ', '+'),
                    firstName: queryParams.get('user_first_name')?.replace('+', ' '),
                    lastName: queryParams.get('user_last_name')?.replace('+', ' '),
                    password: '',
                    confirmPassword: '',
                    phoneNumberCountry: location,
                  }}
                  validationSchema={Yup.object().shape({
                    phoneNumber: Yup.string().required(t('common:requiredField')),
                    email: Yup.string().required(t('common:requiredField')),
                    firstName: Yup.string().required(t('common:requiredField')),
                    lastName: Yup.string().required(t('common:requiredField')),
                    password: Yup.string().required(t('common:requiredField')),
                    confirmPassword: Yup.string().required(t('common:requiredField')),
                    phoneNumberCountry: Yup.string().required(t('common:requiredField')),
                  })}
                  validate={(values) => {
                    if (values.phoneNumberCountry === OTHER_PHONE_COUNTRY) return;
                    const phoneLength = phoneCountryCodes[values.phoneNumberCountry].placeholder.replace(
                      /[() -]/g,
                      ''
                    ).length;
                    const isBelgium = values.phoneNumberCountry === 'be';
                    const minLength = isBelgium ? phoneLength - 1 : phoneLength;
                    if (values.phoneNumber?.toString().length < minLength)
                      return { phoneNumber: t('common:signup.form.phoneNumber.errors.invalid') };
                  }}
                  onSubmit={(values) => {
                    const { phoneNumberCountry, ...newValues } = values;
                    const mapValuesForBackend = {
                      ...newValues,
                      contactPhoneCountry: phoneNumberCountry,
                      phoneNumber:
                        phoneNumberCountry === OTHER_PHONE_COUNTRY
                          ? `+${values.phoneNumber}`
                          : `+${phoneCountryCodes[phoneNumberCountry].code}${values.phoneNumber}`,
                    };
                    submit(mapValuesForBackend);
                  }}
                >
                  {({ isValid, dirty, values, setFieldValue, errors }: FormikProps<any>) => {
                    return (
                      <Form name="form-signup" id="form-signup" trackingEvent={signupEvents.EDIT_FIELD}>
                        <Grid container rowSpacing={1}>
                          <Grid item xs={12}>
                            <TextField
                              name="email"
                              fullWidth
                              disabled
                              placeholder={t('common:signup.form.email.placeholder')}
                              label={t('common:signup.form.email.label')}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label={t('user:signup.firstName.label')}
                              name="firstName"
                              placeholder={t('user:signup.firstName.placeholder')}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label={t('user:signup.lastName.label')}
                              name="lastName"
                              placeholder={t('user:signup.lastName.placeholder')}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={0}>
                              <Grid item xs={4} md={3}>
                                <SelectField
                                  label={t('common:signup.form.phoneNumber.placeholder')}
                                  name="phoneNumberCountry"
                                  options={[
                                    ...Object.entries(phoneCountryCodes)
                                      .map(([key, value]) => ({
                                        value: key,
                                        label: value.code.toString(),
                                      }))
                                      .sort((a, b) => a.value.localeCompare(b.value)),
                                    {
                                      value: OTHER_PHONE_COUNTRY,
                                      label: t('common:signup.form.phoneNumber.otherOption'),
                                    },
                                  ]}
                                  renderOption={({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                      {value !== OTHER_PHONE_COUNTRY && (
                                        <CountryFlag
                                          countryCode={value}
                                          size={24}
                                          style={{ marginRight: '8px' }}
                                        />
                                      )}
                                      {value === OTHER_PHONE_COUNTRY ? label : `(+${label})`}
                                    </MenuItem>
                                  )}
                                  renderValue={(value) => {
                                    return (
                                      <Typography textAlign="center">
                                        {value === OTHER_PHONE_COUNTRY
                                          ? t('common:signup.form.phoneNumber.otherOption')
                                          : `(+${phoneCountryCodes[value as CountryKeys].code})`}
                                      </Typography>
                                    );
                                  }}
                                />
                              </Grid>
                              <Grid item xs={8} md={9}>
                                {values.phoneNumberCountry === OTHER_PHONE_COUNTRY ? (
                                  <TextField
                                    name="phoneNumber"
                                    fullWidth
                                    label={
                                      <span style={{ visibility: 'hidden' }}>
                                        {t('common:signup.form.phoneNumber.placeholder')}
                                      </span>
                                    }
                                    placeholder={t('common:signup.form.phoneNumber.otherPlaceholder')}
                                    onChange={(value: string) =>
                                      setFieldValue('phoneNumber', value.replace(/\D/g, ''))
                                    }
                                  />
                                ) : (
                                  <PhoneNumberField
                                    name="phoneNumber"
                                    label="Phone number"
                                    hideLabel
                                    fullWidth
                                    value={values.phoneNumber}
                                    format={phoneCountryCodes[
                                      values.phoneNumberCountry as CountryKeys
                                    ].placeholder.replace(/[0-9]/g, '#')}
                                    placeholder={
                                      phoneCountryCodes[values.phoneNumberCountry as CountryKeys].placeholder
                                    }
                                    onChange={(value) => setFieldValue('phoneNumber', value.floatValue)}
                                  />
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              type="password"
                              fullWidth
                              name="password"
                              label={t('common:createPassword.passwordLabel')}
                              placeholder={t('common:createPassword.passwordPlaceholder')}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              type="password"
                              fullWidth
                              name="confirmPassword"
                              label={t('common:createPassword.confirmPasswordLabel')}
                              placeholder={t('common:createPassword.confirmPasswordPlaceholder')}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container sx={{ marginTop: '1em', textAlign: 'left' }} rowSpacing={1}>
                              <Grid item xs={12} sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                {values?.password &&
                                values.password.length >= 8 &&
                                values.password.length <= 20 ? (
                                  <CheckCircleOutlineIcon />
                                ) : (
                                  <RadioButtonUncheckedIcon />
                                )}
                                <Typography>{t('common:createPassword.passwordLengthCriteria')}</Typography>
                              </Grid>
                              <Grid item xs={12} sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                {values?.password &&
                                hasUpperCase(values.password) &&
                                hasLowerCase(values.password) ? (
                                  <CheckCircleOutlineIcon />
                                ) : (
                                  <RadioButtonUncheckedIcon />
                                )}
                                <Typography>
                                  {t('common:createPassword.passwordUpperAndLowercaseCriteria')}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                {values?.password && hasDigit(values.password) ? (
                                  <CheckCircleOutlineIcon />
                                ) : (
                                  <RadioButtonUncheckedIcon />
                                )}
                                <Typography>{t('common:createPassword.passwordDigitCriteria')}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          {!!error && (
                            <Grid item xs={12}>
                              <ErrorMessage errorMessage={t(`common:signup.form.errors.${error}`)} />
                            </Grid>
                          )}
                          <Grid item xs={12} sx={{ paddingTop: '2em !important' }}>
                            <Button
                              fullWidth
                              color="secondary"
                              size={ButtonSizes.Large}
                              isLoading={isLoading}
                              disabled={!dirty || !isValid}
                              isSubmit
                            >
                              {t('common:signup.form.submit')}
                            </Button>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: '8px',
                              paddingTop: '1em !important',
                            }}
                          >
                            <Typography>{t('common:signup.alreadyHaveAnAccount')}</Typography>
                            <Link to={'/login'} style={{ color: COLORS.indigo[500] }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  lineHeight: '24px',
                                  textAlign: 'left',
                                  color: COLORS.indigo[500],
                                }}
                              >
                                {t('common:signup.login')}
                              </Typography>
                            </Link>
                          </Grid>
                        </Grid>
                      </Form>
                    );
                  }}
                </Formik>
              </Grid>
              <Grid item xs={10} md={8}>
                <TermsAndConditionsLabel
                  firstPart={`${t('common:signup.termsAndConditionsFirstPart')} `}
                  sx={{ textAlign: 'center' }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default InvitationSignup;
